/**
 * Logika murni ganti password massal (siswa & guru), tanpa React.
 *
 * Batas dan pesan validasi di sini HARUS sama dengan parser backend
 * (`backend/src/apps/DashboardSchool/bulkPassword/parseBulkPasswordRequest.js`):
 * maksimal 200 ID per permintaan, password 6–64 karakter tanpa spasi di
 * awal/akhir. Bila backend berubah, ubah keduanya.
 */

export const BULK_PASSWORD_CHUNK = 200;
export const PASSWORD_MIN_LENGTH = 6;
export const PASSWORD_MAX_LENGTH = 64;

const MESSAGE_REQUIRED = 'Password baru harus diisi.';
const MESSAGE_LENGTH = 'Password harus 6–64 karakter.';
const MESSAGE_SPACES = 'Password tidak boleh diawali atau diakhiri spasi.';

const SKIP_REASON_LABELS = {
  NO_NISN: 'tidak punya NISN',
  NO_NIP: 'tidak punya NIP',
};

export const NOT_FOUND_LABEL = 'tidak ditemukan di sekolah ini';

/** Angka dengan pemisah ribuan Indonesia (1.525). */
export const formatCount = (value) => Number(value).toLocaleString('id-ID');

/**
 * @param {string[]} ids
 * @param {number} [size]
 * @returns {string[][]}
 */
export function chunkIds(ids, size = BULK_PASSWORD_CHUNK) {
  if (!Number.isInteger(size) || size < 1) {
    throw new Error(`Ukuran potongan tidak sah: ${size}`);
  }
  const chunks = [];
  for (let start = 0; start < ids.length; start += size) {
    chunks.push(ids.slice(start, start + size));
  }
  return chunks;
}

/**
 * @param {unknown} value
 * @returns {string | null} pesan galat, atau null bila sah
 */
export function validateBulkPassword(value) {
  if (typeof value !== 'string' || value === '') return MESSAGE_REQUIRED;
  if (
    value.length < PASSWORD_MIN_LENGTH ||
    value.length > PASSWORD_MAX_LENGTH
  ) {
    return MESSAGE_LENGTH;
  }
  if (value !== value.trim()) return MESSAGE_SPACES;
  return null;
}

const defaultValueOf = (row, getDefault) => String(getDefault(row) ?? '');

/**
 * Akun yang PUNYA `account.defaultPassword` dan nilainya beda dari NISN/NIP.
 * Akun tanpa `defaultPassword` tidak dihitung: statusnya tidak diketahui.
 *
 * @param {object[]} rows
 * @param {(row: object) => unknown} getDefault
 * @returns {number}
 */
export function countNonDefault(rows, getDefault) {
  return rows.filter((row) => {
    const stored = row?.account?.defaultPassword;
    if (stored === null || stored === undefined) return false;
    return String(stored) !== defaultValueOf(row, getDefault);
  }).length;
}

/**
 * @param {object[]} rows
 * @param {(row: object) => unknown} getDefault
 * @returns {number} baris yang NISN/NIP-nya kosong
 */
export function countMissingDefault(rows, getDefault) {
  return rows.filter((row) => defaultValueOf(row, getDefault).trim() === '')
    .length;
}

/**
 * @param {string} reason kode alasan dari backend
 * @returns {string} label UI; kode tak dikenal dikembalikan apa adanya
 */
export function skipReasonLabel(reason) {
  return SKIP_REASON_LABELS[reason] ?? reason;
}

/**
 * Daftar yang dilewati untuk layar hasil: `skipped` lalu `notFound`.
 *
 * @param {{ skipped: {id: string, reason: string}[], notFound: string[] }} result
 * @param {Map<string, object>} rowsById
 * @param {(row: object) => string} getName
 * @returns {{ id: string, name: string, reason: string }[]}
 */
export function describeSkipped({ skipped, notFound }, rowsById, getName) {
  const nameOf = (id) => {
    const row = rowsById.get(String(id));
    return row ? getName(row) : String(id);
  };
  return [
    ...skipped.map(({ id, reason }) => ({
      id,
      name: nameOf(id),
      reason: skipReasonLabel(reason),
    })),
    ...notFound.map((id) => ({
      id,
      name: nameOf(id),
      reason: NOT_FOUND_LABEL,
    })),
  ];
}

/**
 * Gabungkan hasil awal dengan hasil "Coba lagi yang gagal".
 * `failedIds` diganti seluruhnya: yang dicoba ulang hanya ID gagal.
 */
export function mergeRetryResult(previous, retry) {
  return {
    updated: previous.updated + retry.updated,
    skipped: [...previous.skipped, ...retry.skipped],
    notFound: [...previous.notFound, ...retry.notFound],
    failedIds: [...retry.failedIds],
  };
}

const isChunkResponse = (response) =>
  typeof response?.updated === 'number' &&
  Array.isArray(response.skipped) &&
  Array.isArray(response.notFound);

async function submitOneChunk({ chunk, mode, password, submitChunk, result }) {
  try {
    const response = await submitChunk({ ids: chunk, mode, password });
    if (!isChunkResponse(response)) {
      throw new Error('Respons ganti password tidak dikenali.');
    }
    return {
      ...result,
      updated: result.updated + response.updated,
      skipped: [...result.skipped, ...response.skipped],
      notFound: [...result.notFound, ...response.notFound],
    };
  } catch {
    return { ...result, failedIds: [...result.failedIds, ...chunk] };
  }
}

/**
 * Kirim ID per potongan, BERURUTAN. Potongan yang melempar (jaringan/5xx)
 * atau membalas dengan bentuk tak dikenal dicatat di `failedIds`, lalu
 * potongan berikutnya tetap dikirim. Operasinya idempoten, jadi aman diulang.
 *
 * @param {{
 *   ids: string[],
 *   mode: 'default' | 'custom',
 *   password: string | null,
 *   submitChunk: (payload: { ids: string[], mode: string, password: string | null })
 *     => Promise<{ updated: number, skipped: {id: string, reason: string}[], notFound: string[] }>,
 *   onProgress?: (processedCount: number, total: number) => void,
 * }} options
 */
export async function runBulkPasswordChunks({
  ids,
  mode,
  password,
  submitChunk,
  onProgress = () => {},
}) {
  let result = { updated: 0, skipped: [], notFound: [], failedIds: [] };
  let processed = 0;

  // eslint-disable-next-line no-restricted-syntax
  for (const chunk of chunkIds(ids)) {
    // Berurutan dengan sengaja: satu permintaan pada satu waktu.
    // eslint-disable-next-line no-await-in-loop
    result = await submitOneChunk({
      chunk,
      mode,
      password,
      submitChunk,
      result,
    });
    processed += chunk.length;
    onProgress(processed, ids.length);
  }
  return result;
}
